import React, { useEffect, useState } from 'react';
import Card from '../Card';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import PatchHeader from './patchHeader';
import { useActivityRetry, useActivityRollback, useRemedActivity } from '../services/activityService';
import Pagination from '../../components/Pagination/Pagination';
import { LoaderCircle } from 'lucide-react';
import { useTenantSelection } from '../../providers/TenantSelectionProvider';
import { useToast } from '../../providers/ToastProvider/ToastContext';

type Activity = {
  id: number;
  appIcon: string;
  activity: string;
  patches: number;
  endpoints: number;
  failedDevices: number;
  successPercentage: number;
  agentIds: string[];
};

interface PaginationInfo {
  pageNumber: number;
  pageSize: number;
  totalRecords: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

const ITEMS_PER_PAGE = 6;
type Mode = 'OnDemand' | 'Automated';

const StatusCard: React.FC<Activity & { tenantID: string; mode: string }> = ({
  id,
  appIcon,
  activity,
  patches,
  endpoints,
  failedDevices,
  successPercentage,
  agentIds,
  tenantID,
  mode,
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { getRollbackActivity } = useActivityRollback();
  const { retryActivity } = useActivityRetry();
  const { addToast } = useToast();
  const [rollbackLoading, setRollbackLoading] = useState(false);
  const [retryLoading, setRetryLoading] = useState(false);

  const handleRollback = async () => {
    try {
      setRollbackLoading(true);
      const res = await getRollbackActivity(tenantID, id.toString(), agentIds);

      console.log('Rollback success:', res);

      addToast(`Rollback initiated successfully for "${activity}".`, 'success', 4000, 'Rollback Successful');
    } catch (error: any) {
      console.error('Rollback failed:', error);
    } finally {
      setRollbackLoading(false);
    }
  };

  const handleRetry = async () => {
    try {
      setRetryLoading(true);
      const res = await retryActivity(id.toString());
      console.log('Retry success:', res);

      addToast(`${res.message}.`, 'success', 4000, 'Retry');
    } catch (error: any) {
      console.error('Retry failed:', error);
    } finally {
      setRetryLoading(false);
    }
  };

  return (
    <Card>
      <div className="rounded-xl shadow p-5 flex flex-col" style={{ backgroundColor: '#1C1C1D' }}>
        <div className="flex items-center gap-2 mb-2">
          <span className="w-9 h-9 flex items-center justify-center rounded-full bg-[#2a2a2a] text-white text-xs font-semibold border border-gray-500">
            {appIcon || 'NA'}
          </span>
          <span className="text-gray-300 text-sm font-medium truncate">{activity}</span>
        </div>

        <div className="w-full border-t border-gray-600 my-3 mb-5"></div>

        <div className="text-sm text-gray-400 space-y-1 mb-3">
          <div className="flex justify-between">
            <span>Number of Patches</span>
            <span className="w-7 h-7 flex items-center justify-center rounded-full bg-[#2a2a2a] text-white text-xs font-semibold border border-gray-500">
              {patches}
            </span>
          </div>
          <div className="flex justify-between">
            <span>Number of Endpoints</span>
            <span className="w-7 h-7 flex items-center justify-center rounded-full bg-[#2a2a2a] text-white text-xs font-semibold border border-gray-500">
              {endpoints}
            </span>
          </div>
          <div className="flex justify-between">
            <span>Number of Devices Failed</span>
            <span className="w-7 h-7 flex items-center justify-center rounded-full bg-[#2a2a2a] text-white text-xs font-semibold border border-gray-500">
              {failedDevices}
            </span>
          </div>
        </div>

        <div className="mb-3 mt-2">
          <div className="flex items-center justify-between text-xs text-gray-400 mb-1">
            <span>Success</span>
            <span className="text-white font-bold">{successPercentage}%</span>
          </div>
          <div className="w-full h-2 bg-gray-700 rounded-full">
            <div
              className="h-2 rounded-full transition-all"
              style={{
                width: `${successPercentage}%`,
                background: '#0DCB39',
              }}
            />
          </div>
        </div>

        <div className="flex gap-2 mt-auto">
          <button
            className="bg-red-600 hover:bg-red-700 text-white rounded-full px-4 py-1 text-sm font-semibold"
            onClick={() => navigate(`/patchcenter/RemediationReport/${id}?${searchParams.toString()}`)}
          >
            View Report
          </button>

          {/* ✅ Retry button */}
          {mode !== 'Automated' && successPercentage !== 100 && (
            <button
              className="bg-gray-700 hover:bg-gray-600 text-white rounded-full px-4 py-1 text-sm disabled:opacity-50 flex items-center justify-center gap-1"
              onClick={handleRetry}
              disabled={retryLoading}
            >
              {retryLoading ? <LoaderCircle className="w-4 h-4 animate-spin" /> : 'Retry failed'}
            </button>
          )}

          {/* ✅ Rollback button with loader */}
          {mode !== 'Automated' && (
            <button
              className="bg-gray-700 hover:bg-gray-600 text-white rounded-full px-4 py-1 text-sm disabled:opacity-50 flex items-center justify-center gap-1"
              onClick={handleRollback}
              disabled={rollbackLoading}
            >
              {rollbackLoading ? <LoaderCircle className="w-4 h-4 animate-spin" /> : 'Roll back'}
            </button>
          )}
        </div>
      </div>
    </Card>
  );
};

interface ActivityPatchBaseProps {
  viewFunction: (view: number) => void;
  mode: Mode;
  setMode: (mode: Mode) => void;
}

const ActivityPatchBase: React.FC<ActivityPatchBaseProps> = ({ viewFunction, mode, setMode }) => {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [paginationInfo, setPaginationInfo] = useState<PaginationInfo | null>(null);

  const { getRemedActivity } = useRemedActivity();
  const { selectedTenantId } = useTenantSelection();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const statuses = queryParams.get('action') ?? undefined;
  const tenantID = selectedTenantId;

  useEffect(() => {
    if (!tenantID) return;

    const fetchActivities = async () => {
      try {
        setLoading(true);
        const response = await getRemedActivity(tenantID, currentPage, ITEMS_PER_PAGE, mode, statuses);

        const mappedData: Activity[] = response.data.map((item: any) => ({
          id: item.remediationPlanID,
          appIcon: item.appIcon || 'NA',
          activity: item.planName || item.activity || 'N/A',
          patches: item.remediationPlanPatchCount ?? 0,
          endpoints: item.totalAgent ?? 0,
          failedDevices: item.failedAgents ?? 0,
          successPercentage: item.totalAgent ? Math.round((item.completedAgent / item.totalAgent) * 100) : 0,
          agentIds: item.remediationPlanAgentIDs ?? [],
        }));

        setActivities(mappedData);
        setPaginationInfo(response.pagination ?? null);
      } catch (error) {
        console.error('Error fetching remediation activities:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchActivities();
  }, [tenantID, currentPage, mode]);

  const totalPages = paginationInfo?.totalPages ?? 1;

  const handlePageChange = (page: number) => {
    if (page !== currentPage) setCurrentPage(page);
  };

  return (
    <div className="flex-1" style={{ backgroundColor: 'rgb(7, 7, 7)' }}>
      <div className="flex flex-1 flex-col p-6">
        <PatchHeader view={0} viewFunction={viewFunction} mode={mode} setMode={setMode} />

        {loading ? (
          <LoaderCircle className="w-8 h-8 mx-auto animate-spin text-gray-400" />
        ) : activities.length === 0 ? (
          <div className="text-gray-400 p-4">No activities found</div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-5">
              {activities.map((activity) => (
                <StatusCard key={activity.id} {...activity} tenantID={tenantID as string} mode={mode} />
              ))}
            </div>

            <div className="mt-6 flex justify-center">
              <Pagination currentPage={currentPage} totalPages={totalPages} loading={loading} onPageChange={handlePageChange} />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ActivityPatchBase;
